import { marketingRequest } from '@/helpers/request'
import Sentry from '@/modules/Sentry'

class Pixel {
	constructor(globalVariableName) {
		this.globalVariableName = globalVariableName
	}

	get instance() {
		const windowPixelObject = window[this.globalVariableName]

		if (!windowPixelObject) {
			console.warn(`${this.globalVariableName} is not initialized`)
		}

		return windowPixelObject
	}
}

class GooglePixel extends Pixel {
	lastBeginCheckoutValue = null
	lastSelectedPlanValue = null

	trackSelectedPlanValue({ price, currency, title } = {}) {
		// title может быть строкой или объектом { text: string, uppercase?: boolean }
		const titleText = typeof title === 'object' ? title?.text : title
		const cleanedTitle = titleText?.replace(/<[^>]*>/g, '').trim()

		// Дедуп подряд идущих одинаковых пушей: на пейволе может быть несколько
		// plan_picker'ов, каждый репортит дефолтный план при маунте — без дедупа
		// add_to_cart (GTM) задвоится для одного выбора (DEV-33512)
		const valueKey = `${price}:${currency}:${cleanedTitle}`
		if (valueKey === this.lastSelectedPlanValue) {
			return
		}
		this.lastSelectedPlanValue = valueKey

		this.instance?.push({
			event: 'updateGtagData',
			value_selected: price,
			currency,
			plan_title: cleanedTitle,
		})
	}

	// Сбрасывается при входе на пейвол: дедуп должен подавлять только одновременные
	// пуши нескольких plan_picker'ов в рамках одного показа, а не одинаковый дефолтный
	// план на разных пейволах / при возврате на пейвол в одной SPA-сессии (DEV-33512)
	resetSelectedPlanValue() {
		this.lastSelectedPlanValue = null
	}

	trackPurchase({ price, priceUSD, isCommitment } = {}) {
		this.instance?.push({
			event: 'updateGtagData',
			value_fact: price,
			value_actual: priceUSD,
		})

		this.instance?.push({
			event: 'predict-purchase-pay',
			target: 'purchase',
			action: 'pay',
		})

		if (isCommitment) {
			this.instance?.push({
				event: 'purchase_commitment',
			})
		} else {
			this.instance?.push({
				event: 'purchase_short',
			})
		}
	}

	trackUpsellPurchase({ price, priceUSD }) {
		this.instance?.push({
			event: 'purchase_upsale',
			value_fact: price,
			value_actual: priceUSD,
		})
	}

	async trackFinalPurchase({ userId, callback, isCommitment } = {}) {
		let pltv = null
		try {
			const response = await marketingRequest(`actions/purchase_track`, { method: 'get', params: { user_id: userId } })
			const result = Number(response)

			if (result) {
				pltv = result
			} else {
				Sentry.withScope((scope) => {
					scope.setLevel('warning')

					scope.setExtra('response', response)
					scope.setExtra('responseType', typeof response)
					scope.setExtra('resultNumber', result)
					scope.setExtra('resultNumberType', typeof result)
					scope.setExtra('pltv', pltv)

					scope.captureMessage('trackFinalPurchase warning')
				})
			}
		} catch (e) {
			Sentry.withScope((scope) => {
				scope.setTags({
					finalPurchaseError: true,
				})
				scope.captureException(e)
			})
		} finally {
			this.instance?.push({
				event: 'updateGtagData',
				value_predict_internal: pltv,
			})
			this.instance?.push({
				event: 'purchase_final',
			})

			if (isCommitment) {
				this.instance?.push({
					event: 'purchase_final_commitment',
				})
			} else {
				this.instance?.push({
					event: 'purchase_final_short',
				})
			}
		}
		if (typeof callback === 'function') {
			callback()
		}
	}

	trackPaywalShown() {
		this.instance?.push({ event: 'paywall_shown' })
	}

	trackBeginCheckout() {
		// Читаем текущий value_selected из dataLayer
		const currentValueSelected = this.instance?.reduce((acc, item) => {
			if (item.value_selected !== undefined) {
				return item.value_selected
			}
			return acc
		}, null)

		// Отправляем событие только если value_selected изменился
		if (currentValueSelected !== this.lastBeginCheckoutValue) {
			this.instance?.push({ event: 'begin_checkout' })
			this.lastBeginCheckoutValue = currentValueSelected
		}
	}

	trackSubmitCheckout() {
		this.instance?.push({ event: 'submit_checkout' })
	}
}

const googlePixel = new GooglePixel('dataLayer')

export { googlePixel }
